import type { Template } from '@/types'
import { normalizeTagsForCompare } from '@/lib/templateTags'

const PREFIX = 'reportStudio.templateDraft.'

export type TemplateDraftEnvelope = {
  savedAt: number
  template: Partial<Template>
}

export function templateDraftKey(id: string | undefined): string {
  return `${PREFIX}${id ?? 'new'}`
}

export function saveTemplateDraft(id: string | undefined, template: Partial<Template>): void {
  const envelope: TemplateDraftEnvelope = { savedAt: Date.now(), template }
  try {
    localStorage.setItem(templateDraftKey(id), JSON.stringify(envelope))
  } catch {
    // quota / private mode
  }
}

export function loadTemplateDraft(id: string | undefined): TemplateDraftEnvelope | null {
  try {
    const raw = localStorage.getItem(templateDraftKey(id))
    if (!raw) return null
    return JSON.parse(raw) as TemplateDraftEnvelope
  } catch {
    return null
  }
}

export function clearTemplateDraft(id: string | undefined): void {
  try {
    localStorage.removeItem(templateDraftKey(id))
  } catch {
    // ignore
  }
}

/** Normalized snapshot for comparing draft partials to a full server template. */
export function normalizeTemplateForCompare(t: Partial<Template>): Record<string, unknown> {
  return {
    name: (t.name ?? '').trim(),
    code: (t.code ?? '').toUpperCase().trim(),
    htmlContent: t.htmlContent ?? '',
    requiredDataSourceKeys: [...(t.requiredDataSourceKeys ?? [])].sort(),
    tags: normalizeTagsForCompare(t.tags),
    excelConfigJson: t.excelConfigJson ?? null,
  }
}

export function templateDraftMatchesServer(draft: Partial<Template>, server: Template): boolean {
  return (
    JSON.stringify(normalizeTemplateForCompare(draft)) ===
    JSON.stringify(normalizeTemplateForCompare(server))
  )
}
